import { Fragment, useCallback, useEffect, useState, type SetStateAction } from "react";
import { Kalender } from "./Kalender";
import { opprettProduksjon, type Lederrolle } from "../../state/oppstart";
import { leggTilDag } from "../../state/oppsett";
import * as logo from "../../state/logo";
import {
  hentFagfunksjoner, kreverInvitasjon, sendInvitasjon, type Fagfunksjon,
} from "../../state/fagfunksjoner";
import { supabase } from "../../state/supabase";
import {
  glemUtkast, hentUtkast, lagreUtkast, mittNavn, mittSelskap,
} from "../../state/konsoll";

/**
 * Å sette opp en produksjon, steg for steg.
 *
 * ========  HVORFOR EN VEIVISER OG IKKE ETT SKJEMA  ========
 *
 * Brukeren 30.10.2026: «Jeg ønsker at vi gjør dette steg for steg, så man
 * fyller ut noe, så trykker man neste.» Det er riktig her av en grunn som
 * ikke gjelder alle skjemaer: de tre stegene er tre ULIKE handlinger —
 * navngi, planlegge dager, invitere folk — og det midterste er en
 * kalender som trenger hele bredden. Presset sammen i ett skjema ville
 * kalenderen stått og dominert to felt den ikke har noe med å gjøre.
 *
 * ========  PRODUKSJONEN BLIR TIL PÅ SISTE STEG  ========
 *
 * Ingenting skrives før man er ferdig. Avbryter man på steg to, finnes
 * det ingen halv produksjon noen må rydde bort — og `opprett_produksjon`
 * gjør selskap, produksjon og medlemskap i én transaksjon nettopp for å
 * slippe den tilstanden.
 *
 * Dagene og invitasjonene skrives ETTER at produksjonen finnes, fordi de
 * peker på den. Feiler en av dem, står produksjonen igjen — og det er
 * riktig vei å feile: dagene legges inn på nytt i Oppsett, mens en
 * produksjon som ikke ble til må settes opp fra bunnen.
 */
export function NyProduksjon({ paaFerdig, paaAvbryt, minEpost }: {
  readonly paaFerdig: (id: string) => void;
  readonly paaAvbryt: () => void;
  /** Din egen adresse. Den kan ikke inviteres — se StegInvitasjoner. */
  readonly minEpost: string;
}): JSX.Element {
  const [steg, setSteg] = useState(0);
  /* Retningen styrer animasjonen: ny side kommer fra hoeyre naar man gaar
     fram, fra venstre naar man gaar tilbake. Uten den ville «Tilbake»
     foeltes som enda et skritt framover. */
  const [bakover, setBakover] = useState(false);

  const utkast = useState(() => hentUtkast())[0];

  const [navn, setNavn] = useState(utkast?.navn ?? "");
  const [logofil, setLogofil] = useState<File | null>(null);
  const [logourl, setLogourl] = useState<string | null>(null);
  const [rolle, setRolle] = useState<Lederrolle>(utkast?.rolle ?? "prodleder");
  const [fra, setFra] = useState(utkast?.fra ?? "");
  const [til, setTil] = useState(utkast?.til ?? "");
  const [dager, setDager] = useState<ReadonlySet<string>>(
    () => new Set(utkast?.dager ?? []));
  const [invitasjoner, setInvitasjoner] = useState<readonly Invitasjon[]>(
    utkast?.invitasjoner ?? []);

  /*
   * KONTOFELTENE VISES BARE NAAR DE MANGLER.
   *
   * Her sto en feilmelding paa siste steg — «Sett opp selskapet under
   * Kontoen din foerst» — etter at alt annet var fylt ut. Brukeren
   * 30.10.2026: «det er fryktelig irriterende aa faa en feilmelding etter
   * at man har tilsynelatende gjort alt.» Den som mangler dem faar dem
   * som to felt paa foerste steg, og de lagres paa kontoen.
   */
  const [selskapsnavn, setSelskapsnavn] = useState(utkast?.selskapsnavn ?? "");
  const [mittnavn, setMittnavn] = useState(utkast?.mittnavn ?? "");
  const [maaSpoerre, setMaaSpoerre] = useState<
    { selskap: boolean; navn: boolean } | null>(null);

  const [jobber, setJobber] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  /** Satt naar produksjonen er laget men noe ved invitasjonene ikke gikk. */
  const [ferdig, setFerdig] = useState<
    { id: string; uten: readonly string[]; grunn: string | null } | null>(null);

  useEffect(() => {
    void (async () => {
      const [sel, mitt] = await Promise.all([mittSelskap(), mittNavn()]);
      setMaaSpoerre({ selskap: sel.id === null, navn: mitt.trim() === "" });
    })();
  }, []);

  /* Utkastet skrives ved hver endring. Det er billig — fire felt og en
     liste — og alternativet er aa gjette naar det er verdt aa lagre. */
  useEffect(() => {
    lagreUtkast({
      navn, fra, til, rolle, selskapsnavn, mittnavn,
      dager: [...dager],
      invitasjoner: invitasjoner.map((i) => ({ ...i })),
      lagret: Date.now(),
    });
  }, [navn, fra, til, rolle, selskapsnavn, mittnavn, dager, invitasjoner]);

  const gaa = useCallback((til: number): void => {
    setBakover(til < steg);
    setFeil(null);
    setSteg(til);
  }, [steg]);

  const velgLogo = useCallback((fil: File | null): void => {
    if (fil === null) return;
    const galt = logo.sjekkFil(fil);
    if (galt !== null) { setFeil(galt); return; }
    setFeil(null);
    setLogofil(fil);
    setLogourl((forrige) => {
      if (forrige !== null) URL.revokeObjectURL(forrige);
      return URL.createObjectURL(fil);
    });
  }, []);

  useEffect(() => () => {
    if (logourl !== null) URL.revokeObjectURL(logourl);
  }, [logourl]);

  const fullfoer = useCallback((): void => {
    setJobber(true);
    setFeil(null);
    void (async () => {
      const svar = await opprettProduksjon({
        produksjonsnavn: navn, startdato: fra, sluttdato: til, rolle,
        selskapsnavn: maaSpoerre?.selskap === true ? selskapsnavn : "",
        mittnavn: maaSpoerre?.navn === true ? mittnavn : "",
      });
      if (!svar.ok) { setFeil(svar.feil); setJobber(false); return; }
      const id = svar.produksjonId;

      /* Dagene i datorekkefoelge, nummerert fra 1. Rekkefoelgen er
         datoens og ikke avhukingens — se Kalender.tsx. */
      let nr = 1;
      for (const dag of [...dager].sort()) {
        await leggTilDag(id, String(nr), dag);
        nr += 1;
      }

      if (logofil !== null) {
        await logo.lastOpp("produksjon", id, logofil, null);
      }

      /* Invitasjonene til slutt. Feiler en av dem, staar produksjonen
         igjen og raden med den — den sendes paa nytt fra Oppsett, der
         «ikke sendt» staar med en knapp. */
      const uten: string[] = [];
      let grunn: string | null = null;
      for (const inv of invitasjoner) {
        if (inv.epost.trim() === "" || inv.craftId === "") continue;
        const laget = await inviterTil(id, inv);
        if (laget.id === null) {
          uten.push(inv.epost.trim());
          grunn ??= laget.feil;
          continue;
        }
        const sendt = await sendInvitasjon(laget.id);
        if (!sendt.ok) {
          uten.push(inv.epost.trim());
          /* GRUNNEN, IKKE BARE ADRESSENE.
             Foerste utgave samlet adressene og kastet feilteksten, og
             viste en `window.alert` med en liste. Da sto prodleder med
             «gikk ikke ut» og ingen anelse om hvorfor — mens funksjonen
             hadde sagt det rett ut. */
          grunn ??= sendt.feil ?? null;
        }
      }

      /* Produksjonen finnes. Da er utkastet ikke lenger et utkast. */
      glemUtkast();
      setJobber(false);

      if (uten.length === 0) { paaFerdig(id); return; }

      /* Produksjonen FINNES, saa dette er ikke en feil som skal sende
         noen tilbake til skjemaet. Den blir staaende i vinduet med
         grunnen, og man gaar videre naar man har lest den. */
      setFerdig({ id, uten, grunn });
    })();
  }, [navn, fra, til, rolle, dager, logofil, invitasjoner, paaFerdig,
      maaSpoerre, selskapsnavn, mittnavn]);

  /*
   * DIN EGEN ADRESSE KAN IKKE INVITERES.
   *
   * Du er alt med i produksjonen — det er du som oppretter den — og
   * grunnmuren gir en person en rad per produksjon. En invitasjon paa
   * din egen adresse kunne derfor aldri bindes, og den smalt foerst ved
   * neste innlogging, langt fra det man gjorde galt:
   *
   *     duplicate key value violates unique constraint
   *     "production_member_production_id_user_id_key"
   *
   * Den stanses her, paa raden, mens man staar i den. Se migrasjon
   * 20261101090000 — basen nekter ogsaa, men det er for sent til aa
   * vaere en god beskjed.
   */
  const minAdresse = minEpost.trim().toLowerCase();
  const megSelv = minAdresse !== "" && invitasjoner.some(
    (i) => i.epost.trim().toLowerCase() === minAdresse);

  const kanVidere = steg === 0
    ? navn.trim() !== ""
      && (maaSpoerre?.selskap !== true || selskapsnavn.trim() !== "")
      && (maaSpoerre?.navn !== true || mittnavn.trim() !== "")
    : steg === 1 ? til !== "" && (fra === "" || fra <= til)
    : true;

  return (
    <div className="vindu" role="dialog" aria-modal="true" aria-label="Ny produksjon">
      {/* Teppet graaner flata bak og tar klikk utenfor. Et halvferdig
          oppsett skal ikke forsvinne fordi man bommet med musa, saa det
          lukker ikke — det er «Avbryt» som gjoer det. */}
      <div className="vindu__teppe" aria-hidden="true" />

      <div className="vindu__boks">
        <header className="vindu__topp">
          <span className="vindu__tittel">Ny produksjon</span>
          <span className="vindu__steg">
            {ferdig === null ? `${steg + 1} av 3` : "ferdig"}
          </span>
          {ferdig !== null ? null : (
            <button type="button" className="innlogging__tilbake" onClick={paaAvbryt}>
              Avbryt
            </button>
          )}
        </header>

        <div className="vindu__spor">
          {ferdig !== null ? (
            <div className="vindu__side">
              <p className="merknad vindu__utkastmerknad">
                Produksjonen er opprettet.
              </p>
              <span className="oppstart__merke">Invitasjonen gikk ikke ut til</span>
              {ferdig.uten.map((e) => (
                <span key={e} className="meny__lesefelt">{e}</span>
              ))}
              {ferdig.grunn === null ? null : (
                <p className="innlogging__feil">{ferdig.grunn}</p>
              )}
              <p className="merknad">
                Kontoene er opprettet, og tilgangen henger ikke i e-posten
                — de kommer inn ved å logge på med adressen sin. Send
                beskjeden på nytt under Oppsett · Produksjon, der raden står
                som «Ikke sendt».
              </p>
            </div>
          ) : (
          <div
            key={steg}
            className={`vindu__side${bakover ? " vindu__side--bakfra" : ""}`}
          >
            {steg === 0 ? (
              <StegNavn
                navn={navn} onNavn={setNavn}
                logourl={logourl} onLogo={velgLogo}
                harUtkast={utkast !== null}
                spoerSelskap={maaSpoerre?.selskap === true}
                selskapsnavn={selskapsnavn} onSelskapsnavn={setSelskapsnavn}
                spoerNavn={maaSpoerre?.navn === true}
                mittnavn={mittnavn} onMittnavn={setMittnavn}
              />
            ) : steg === 1 ? (
              <StegDager
                fra={fra} onFra={setFra} til={til} onTil={setTil}
                dager={dager} onDager={setDager}
              />
            ) : (
              <StegInvitasjoner
                rader={invitasjoner} onRader={setInvitasjoner}
                rolle={rolle} onRolle={setRolle}
                minEpost={minEpost}
              />
            )}
          </div>
          )}
        </div>

        {feil === null ? null : <p className="innlogging__feil">{feil}</p>}

        <footer className="vindu__bunn">
          {ferdig !== null ? (
            <>
              <span />
              <button
                type="button"
                className="synk__knapp synk__knapp--hoved"
                onClick={() => paaFerdig(ferdig.id)}
              >
                Gå til produksjonen
              </button>
            </>
          ) : (
          <>
          {steg === 0 ? <span /> : (
            <button
              type="button"
              className="synk__knapp"
              disabled={jobber}
              onClick={() => gaa(steg - 1)}
            >
              Tilbake
            </button>
          )}
          {steg < 2 ? (
            <button
              type="button"
              className="synk__knapp synk__knapp--hoved"
              disabled={!kanVidere}
              onClick={() => gaa(steg + 1)}
            >
              Neste
            </button>
          ) : (
            <button
              type="button"
              className="synk__knapp synk__knapp--hoved"
              disabled={jobber || megSelv}
              onClick={fullfoer}
            >
              {jobber ? "Oppretter …" : "Opprett produksjon"}
            </button>
          )}
          </>
          )}
        </footer>
      </div>
    </div>
  );
}

/* ---- steg 1: navn og logo ------------------------------------------- */

function StegNavn({
  navn, onNavn, logourl, onLogo, harUtkast,
  spoerSelskap, selskapsnavn, onSelskapsnavn,
  spoerNavn, mittnavn, onMittnavn,
}: {
  readonly navn: string;
  onNavn(v: string): void;
  readonly logourl: string | null;
  onLogo(f: File | null): void;
  readonly harUtkast: boolean;
  readonly spoerSelskap: boolean;
  readonly selskapsnavn: string;
  onSelskapsnavn(v: string): void;
  readonly spoerNavn: boolean;
  readonly mittnavn: string;
  onMittnavn(v: string): void;
}): JSX.Element {
  return (
    <>
      {!harUtkast ? null : (
        <p className="merknad vindu__utkastmerknad">
          Hentet fram igjen fra et utkast. Logoen må velges på nytt — en
          fil kan ikke lagres mellom to besøk.
        </p>
      )}

      <label className="oppstart__merke">
        Navn på produksjonen
        <input
          className="konsoll__felt" value={navn}
          onChange={(e) => onNavn(e.target.value)}
          placeholder="Øya" autoFocus
        />
      </label>

      {/* Bare naar kontoen mangler dem. Den som har dem fra for ser ikke
          disse feltene i det hele tatt. */}
      {!spoerSelskap ? null : (
        <label className="oppstart__merke">
          Selskap
          <input
            className="konsoll__felt" value={selskapsnavn}
            onChange={(e) => onSelskapsnavn(e.target.value)}
            placeholder="Tokt film og tv" autoComplete="organization"
          />
        </label>
      )}
      {!spoerNavn ? null : (
        <label className="oppstart__merke">
          Navnet ditt
          <input
            className="konsoll__felt" value={mittnavn}
            onChange={(e) => onMittnavn(e.target.value)}
            autoComplete="name"
          />
        </label>
      )}
      {!spoerSelskap && !spoerNavn ? null : (
        <span className="merknad">
          Dette lagres på kontoen din, og spørres ikke om igjen.
        </span>
      )}

      <span className="oppstart__merke">Logo</span>
      <div className="logo__rad">
        <div className="logo__rute">
          {logourl === null
            ? <span className="logo__tom" aria-hidden="true" />
            : <img className="logo__bilde" src={logourl} alt="" />}
        </div>
        <div className="logo__tekst">
          <span className="merknad">
            Vises i appen, på nettflata og øverst på dokumentene. Kan
            legges inn senere.
          </span>
          <span className="merknad">
            PNG uten bakgrunn anbefales — logoen står mot lys flate, mot
            mørk i blackout og mot hvitt papir i rapporten.
          </span>
        </div>
        <div className="logo__knapper">
          <label className="synk__knapp logo__knapp">
            {logourl === null ? "Velg fil" : "Bytt"}
            <input
              className="logo__velger" type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={(e) => { onLogo(e.target.files?.[0] ?? null); e.target.value = ""; }}
            />
          </label>
        </div>
      </div>
    </>
  );
}

/* ---- steg 2: dagene -------------------------------------------------- */

function StegDager({ fra, onFra, til, onTil, dager, onDager }: {
  readonly fra: string; onFra(v: string): void;
  readonly til: string; onTil(v: string): void;
  readonly dager: ReadonlySet<string>;
  onDager(d: ReadonlySet<string>): void;
}): JSX.Element {
  /* Kalenderen bygger hele settet selv — ett valg, et dra, eller alle. */
  const endre = useCallback((neste: ReadonlySet<string>): void => {
    onDager(new Set(neste));
  }, [onDager]);

  return (
    <>
      <div className="vindu__datoer">
        <label className="oppstart__merke">
          Fra
          <input className="konsoll__felt" type="date" value={fra}
                 onChange={(e) => onFra(e.target.value)} />
        </label>
        <label className="oppstart__merke">
          Til
          <input className="konsoll__felt" type="date" value={til}
                 onChange={(e) => onTil(e.target.value)} />
        </label>
      </div>
      <p className="merknad">
        Huk av dagene det faktisk er opptak. Nummeret følger datoen, så
        den første avhukede blir opptaksdag 1. Siste dag styrer når den
        lokale loggen slettes fra crewets telefoner.
      </p>
      <Kalender fra={fra} til={til} valgte={dager} onEndre={endre} />
      <span className="merknad">
        {dager.size === 0 ? "Ingen dager valgt ennå"
          : `${dager.size} ${dager.size === 1 ? "opptaksdag" : "opptaksdager"}`}
      </span>
    </>
  );
}

/* ---- steg 3: invitasjonene ------------------------------------------ */

export interface Invitasjon {
  readonly epost: string;
  readonly craftId: string;
  readonly navn: string;
  /**
   * En av de fire som ALLTID staar der.
   *
   * Brukeren 30.10.2026: «feltene som har spesialtillatelser allerede
   * vises, saa man bare maa fylle ut mail adressen». Fagfunksjonen er
   * da gitt, og raden viser den som en etikett i stedet for en
   * nedtrekksliste. Rader man legger til selv er ikke faste — det er
   * ofte mer enn én redaktoer, og da maa man kunne velge.
   */
  readonly fast: boolean;
}

function StegInvitasjoner({ rader, onRader, rolle, onRolle, minEpost }: {
  readonly rader: readonly Invitasjon[];
  onRader(r: SetStateAction<readonly Invitasjon[]>): void;
  readonly rolle: Lederrolle;
  onRolle(r: Lederrolle): void;
  readonly minEpost: string;
}): JSX.Element {
  const [fag, setFag] = useState<readonly Fagfunksjon[]>([]);

  useEffect(() => {
    void (async () => {
      const alle = await hentFagfunksjoner();
      /* Bare de som KREVER invitasjon. Alle andre velger fagfunksjonen
         sin selv i menyen — det er hele poenget med selvvalget, og en
         liste med 42 valg her ville skjult de fire som betyr noe. */
      const noekler = alle.filter((f) => kreverInvitasjon(f.rolle));
      setFag(noekler);

      /* De fire staar der ferdig, med tom adresse. Den som bare skal
         invitere en redaktoer skal ikke foerst maatte finne ut at det
         finnes en knapp som heter «Legg til en rad». */
      onRader((foer) => {
        if (foer.length > 0) return foer;
        return noekler.map((f) => ({
          epost: "", craftId: f.id, navn: "", fast: true,
        }));
      });
    })();
  }, [onRader]);

  const endre = useCallback((i: number, felt: Partial<Invitasjon>): void => {
    onRader(rader.map((r, n) => (n === i ? { ...r, ...felt } : r)));
  }, [rader, onRader]);

  const minAdresse = minEpost.trim().toLowerCase();
  const erMeg = (e: string): boolean =>
    minAdresse !== "" && e.trim().toLowerCase() === minAdresse;

  return (
    <>
      {/* DIN EGEN ROLLE STAAR FOERST.
          Den laa paa steg én, ved siden av navn og logo, og brukeren
          30.10.2026: «Den hoerer mer hjemme der.» Det stemmer — dette er
          steget der det avgjoeres hvem som har hvilke noekler, og din
          egen er den foerste av dem. */}
      <fieldset className="oppstart__roller vindu__minrolle">
        <legend className="oppstart__merke">Du er</legend>
        {(["prodleder", "produsent"] as const).map((r) => (
          <label key={r} className="oppstart__rolle">
            <input type="radio" name="rolle" checked={rolle === r}
                   onChange={() => onRolle(r)} />
            {r === "prodleder" ? "Produksjonsleder" : "Produsent"}
          </label>
        ))}
      </fieldset>

      <p className="merknad">
        Fyll inn adressen til dem som skal ha nøkler. Raden står klar til
        den som eier adressen logger inn — alle andre velger fagfunksjonen
        sin selv. Tomme rader sendes ikke, og flere kan ha samme
        fagfunksjon.
      </p>

      {/* Uten fagfunksjonene er steget tomt, og et tomt steg uten
          forklaring ser ut som en feil i flata. Produksjonen kan
          fortsatt opprettes — invitasjonene gjoeres da i Oppsett. */}
      {fag.length > 0 ? null : (
        <p className="merknad">
          Fikk ikke hentet fagfunksjonene. Du kan opprette produksjonen
          likevel og invitere under Oppsett.
        </p>
      )}

      {rader.map((r, i) => (
        <Fragment key={i}>
        <div className="vindu__invitasjon">
          <span className="vindu__fag">
            {r.fast
              ? fag.find((f) => f.id === r.craftId)?.navn ?? "Fagfunksjon"
              : (
                <select
                  className="konsoll__felt" value={r.craftId}
                  onChange={(e) => endre(i, { craftId: e.target.value })}
                >
                  <option value="">Fagfunksjon …</option>
                  {fag.map((f) => (
                    <option key={f.id} value={f.id}>{f.navn}</option>
                  ))}
                </select>
              )}
          </span>
          <input
            className="konsoll__felt" type="email" value={r.epost}
            placeholder="navn@selskap.no" autoComplete="off"
            aria-invalid={erMeg(r.epost)}
            onChange={(e) => endre(i, { epost: e.target.value })}
          />
          <input
            className="konsoll__felt" value={r.navn}
            placeholder="Navn (valgfritt)" autoComplete="off"
            onChange={(e) => endre(i, { navn: e.target.value })}
          />
          {/* De faste fjernes ikke — de staar tomme om man ikke har noen
              aa sette der, og en tom rad sendes ikke. */}
          {r.fast ? <span /> : (
            <button
              type="button" className="synk__knapp"
              onClick={() => onRader(rader.filter((_, n) => n !== i))}
            >
              Fjern
            </button>
          )}
        </div>
        {!erMeg(r.epost) ? null : (
          <p className="innlogging__feil">
            Dette er din egen adresse. Du er med i produksjonen fra
            første sekund — velg en annen rolle til deg selv over, og la
            raden stå tom.
          </p>
        )}
        </Fragment>
      ))}

      <button
        type="button"
        className="synk__knapp"
        onClick={() => onRader([
          ...rader, { epost: "", craftId: "", navn: "", fast: false },
        ])}
      >
        Legg til en rad
      </button>
      <span className="merknad">
        Du kan hoppe over dette og invitere senere, under Oppsett.
      </span>
    </>
  );
}

/**
 * Skriver invitasjonsraden.
 *
 * Egen her og ikke `fagfunksjoner.inviter`: den tar en `Oekt`, og den
 * finnes ikke ennå — produksjonen ble til for et øyeblikk siden, og vi
 * er fortsatt i konsollen. `approved_at` settes fordi invitasjonen ER
 * avgjørelsen; den som inviterer har alt bestemt.
 */
async function inviterTil(
  produksjonId: string, inv: Invitasjon,
): Promise<{ id: string | null; feil: string | null }> {
  if (supabase === null) return { id: null, feil: null };
  const navn = inv.navn.trim() === "" ? inv.epost.trim() : inv.navn.trim();
  const { data, error } = await supabase.from("production_member").insert({
    production_id: produksjonId,
    craft_id: inv.craftId,
    invited_email: inv.epost.trim().toLowerCase(),
    display_name: navn,
    initials: navn.split(/\s+/).map((o) => o[0] ?? "").join("").slice(0, 3).toUpperCase(),
    approved_at: new Date().toISOString(),
  }).select("id").maybeSingle();
  /* FEILTEKSTEN BLIR MED.
     Her sto `return null`, og da sto prodleder igjen med en adresse
     under «gikk ikke ut» og ingen grunn — ogsaa naar basen hadde sagt
     det rett ut, slik `vern_dobbelt_medlemskap` gjoer. */
  if (error !== null) return { id: null, feil: error.message };
  return { id: (data as { id?: string } | null)?.id ?? null, feil: null };
}
